import { ForwardedRef, forwardRef, ReactElement, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import MenuIcon from '@mui/icons-material/Menu';
import {
  alpha,
  AppBar,
  AppBarProps,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  Skeleton,
  Stack,
  StackProps,
  Toolbar,
  ToolbarProps,
} from '@mui/material';

import { useDebounce } from 'usehooks-ts';

import { AuthContext } from '@arrived/auth';
import { Constants } from '@arrived/common';
import { FEATURE_FLAGS, useIsFeatureFlagEnabled } from '@arrived/feature-flags';

import { ArrivedPhoneNumber } from '../ArrivedPhoneNumber';

import { UkraineBanner } from '../banners';
import { useIsMobile } from '../hooks';
import { NavItemComponent, NavItemProps, signUpText } from '../navigation';

const NavContainer = (props: StackProps) => <Stack alignItems="center" direction="row" gap={2} {...props} />;

const NavList = (props: StackProps) => <Stack alignItems="center" direction="row" {...props} />;

export enum NavigationBarStyle {
  DEFAULT,
  GRADIENT,
}

export interface NavigationBarProps<T> {
  Component: NavItemComponent<T>;
  primaryItems?: NavItemProps[];
  secondaryItems?: NavItemProps[];
  extraItems?: NavItemProps[];
  centeredItems?: NavItemProps[];
  logoComponent?: ReactNode;
  isTransparent?: boolean;
  hideSignUp?: boolean;
  navigationStyle?: NavigationBarStyle;
  AppBarProps?: Omit<AppBarProps, 'ref'>;
  ToolbarProps?: ToolbarProps;
  mobileLogo: ReactNode;
  desktopLogo: ReactNode;
}

export const NavigationBar = forwardRef(
  <T extends ReactElement>(
    {
      AppBarProps,
      ToolbarProps,
      Component,
      hideSignUp,
      extraItems,
      primaryItems,
      secondaryItems,
      centeredItems,
      mobileLogo,
      desktopLogo,
      navigationStyle = NavigationBarStyle.DEFAULT,
    }: NavigationBarProps<T>,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const { isAuthenticated, isLoading, signup } = useContext(AuthContext);
    const isPhoneContactEnabled = useIsFeatureFlagEnabled(FEATURE_FLAGS.PHONE_CONTACT);
    const isTopBannerEnabled = useIsFeatureFlagEnabled(FEATURE_FLAGS.SHOW_TOP_BANNER);
    const isMobile = useIsMobile();

    const { sx: AppBarPropsSx, ...AppBarPropsRest } = AppBarProps || {};
    const { sx: ToolbarPropsSx, ...ToolbarPropsRest } = ToolbarProps || {};

    const [isOpen, setIsOpen] = useState(false);
    const toggleMobileTopNavOpen = useCallback(() => setIsOpen((isOpen) => !isOpen), []);
    const handleOnClose = useCallback(() => setIsOpen(false), []);

    const debouncedIsOpen = useDebounce(isOpen, 250);

    const { ref: appBarRef, height } = useResizeDetector({ handleHeight: true, handleWidth: true });

    const setRefs = useCallback((element: HTMLDivElement | null) => {
      if (ref) {
        typeof ref === 'function' ? ref(element) : (ref.current = element);
      }
      appBarRef.current = element;
    }, []);

    const mobileItems = useMemo(
      () => [...(primaryItems ?? []), ...(secondaryItems ?? []), ...(extraItems ?? [])],
      [primaryItems, secondaryItems, extraItems]
    );

    return (
      <AppBar
        ref={setRefs}
        sx={{
          overflow: 'hidden',
          background: ({ palette }) =>
            navigationStyle === NavigationBarStyle.GRADIENT
              ? `linear-gradient(90deg, ${palette.aqua[700]}, ${palette.aqua[600]} 100%)`
              : palette.lightened.bare,
          boxShadow: 'unset',
          borderBottom: ({ palette }) => `1px solid ${palette.neutrals.platinum}`,
          // backgroundColor: 'white',
          zIndex: ({ zIndex }) => (isMobile && (isOpen || debouncedIsOpen) ? zIndex.drawer + 1 : undefined),
          // TODO: These styles really belong on the item components themselves, but we'll need to do some other
          //  restructuring to make that happen.
          '& .nav-item': {
            padding: ({ spacing }) => spacing(0.5, 1, 0, 1),
            height: '52px',
            whiteSpace: 'nowrap',
            '&:hover, &.selected-nav': {
              marginBottom: '-2px',
              borderBottom: ({ palette }) => `2px solid ${palette.primary.main}`,
            },
            '&:hover': {
              backgroundColor: ({ palette }) => alpha(palette.text.primary, 0.1),
            },
            '&.selected-nav': {
              backgroundColor: 'unset',
            },
          },
          ...AppBarPropsSx,
        }}
        data-e2e="nav-bar-wrapper"
        {...AppBarPropsRest}
      >
        {isTopBannerEnabled && (
          <UkraineBanner
            imageComponent={
              <Box
                component="img"
                src={`${Constants.imageCdnUrl}/images/logos/arrived-ukraine.png`}
                width={20}
                height={20}
                alt="Arrived stands with Ukraine"
              />
            }
          />
        )}
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2, ...ToolbarPropsSx }} {...ToolbarPropsRest}>
          <NavContainer>
            {isMobile && mobileLogo}
            {!isMobile && desktopLogo}
            {!isMobile && (
              <NavList display="flex">
                {isLoading ? <Skeleton width={300} height={40} /> : primaryItems?.map(Component)}
              </NavList>
            )}
          </NavContainer>
          {centeredItems && <>{centeredItems?.map(Component)}</>}
          {isLoading ? (
            <Skeleton width={100} height={40} />
          ) : (
            <NavContainer>
              {isMobile ? (
                <>
                  {!isAuthenticated && !hideSignUp && (
                    <Button onClick={signup} size="small" sx={{ minWidth: '5rem' }}>
                      {signUpText}
                    </Button>
                  )}
                  <IconButton
                    onClick={toggleMobileTopNavOpen}
                    className="mobile-top-nav-menu-button"
                    size="large"
                    data-e2e="mobile-nav-button"
                    sx={{
                      color: ({ palette }) =>
                        navigationStyle === NavigationBarStyle.GRADIENT ? palette.lightened.bare : undefined,
                      '&:hover': {
                        color: ({ palette }) =>
                          navigationStyle === NavigationBarStyle.GRADIENT ? palette.text.primary : undefined,
                      },
                    }}
                  >
                    <MenuIcon fontSize="large" />
                  </IconButton>
                  <Drawer
                    anchor="top"
                    sx={{
                      '& .MuiDrawer-paper': {
                        marginTop: `${height}px`,
                        maxHeight: `calc(100% - ${height}px)`,
                      },
                    }}
                    open={isOpen}
                    onClose={handleOnClose}
                  >
                    <List
                      // TODO: These styles really belong on the item components themselves, but we'll need to do some
                      //  other restructuring to make that happen.
                      sx={{
                        '& .nav-item-inner': { width: '100%' },
                        '& .MuiAvatar-root': { width: '33px', height: '33px' },
                        '& .nested-list .MuiListItem-root': { pl: 4 },
                        '& .MuiListItem-root, & .MuiListItemButton-root': {
                          '&:hover': { backgroundColor: ({ palette }) => alpha(palette.text.primary, 0.04) },
                          '&.selected-nav': { backgroundColor: ({ palette }) => alpha(palette.text.primary, 0.15) },
                        },
                      }}
                    >
                      {mobileItems.map((item, idx) => (
                        <Component key={idx} onClick={handleOnClose} {...item} />
                      ))}
                    </List>
                  </Drawer>
                </>
              ) : (
                <>
                  {isPhoneContactEnabled && <ArrivedPhoneNumber />}
                  <NavList
                    className="nav-list"
                    sx={{
                      '& .nav-item-inner, & .nav-item': {
                        color: ({ palette }) =>
                          navigationStyle === NavigationBarStyle.GRADIENT ? palette.lightened.bare : undefined,
                      },
                    }}
                    gap={2}
                  >
                    {secondaryItems?.map(Component)}
                  </NavList>
                </>
              )}
            </NavContainer>
          )}
        </Toolbar>
      </AppBar>
    );
  }
);
